import { useState, useRef, useCallback } from "react";
import { useShoppableVideos } from "@/hooks/useApi";
import { apiProductToProduct } from "@/services/adapters";
import { storageUrl } from "@/services/config";
import type { Product, ShoppableVideo } from "@/types";
import VideoShoppingModal from "./VideoShoppingModal";

export default function ShoppableVideosSection() {
  const { data: apiProducts, incrementView } = useShoppableVideos();
  const [selectedVideo, setSelectedVideo] = useState<ShoppableVideo | null>(
    null,
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const products = apiProducts.map((p) => apiProductToProduct(p));

  const scroll = useCallback((direction: "left" | "right") => {
    if (scrollRef.current) {
      const amount = direction === "left" ? -320 : 320;
      scrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  }, []);

  const handlePlayVideo = (product: Product, apiProduct: any) => {
    setSelectedVideo({
      id: product.id,
      title: product.name,
      label: product.category,
      thumbnail: product.image,
      duration: "",
      productId: product.id,
      views: apiProduct?.video_views ? String(apiProduct.video_views) : "0",
      videoUrl: apiProduct?.video ? storageUrl(apiProduct.video) : "",
    });
    if (apiProduct?.id) incrementView(apiProduct.id);
  };

  if (products.length === 0) return null;

  return (
    <section className="py-14 md:py-20 bg-cream-50">
      <div className="container-main">
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="text-center w-full">
            <h2 className="text-2xl md:text-3xl font-bold text-charcoal-900 mb-2">
              شاهدنا عن قرب
            </h2>
            <p className="text-4xl md:text-7xl  text-[#79A286] max-w-lg mx-auto font-['Cartier']">
              See It in Action
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 absolute left-0 bottom-0">
            <button
              onClick={() => scroll("right")}
              className="w-10 h-10 rounded-full border border-cream-300 bg-white flex items-center justify-center text-charcoal-600 hover:bg-charcoal-900 hover:text-white transition-all"
            >
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => scroll("left")}
              className="w-10 h-10 rounded-full border border-cream-300 bg-white flex items-center justify-center text-charcoal-600 hover:bg-charcoal-900 hover:text-white transition-all"
            >
              <i className="ri-arrow-left-line" />
            </button>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 md:gap-5 overflow-x-auto pb-4 px-4 md:px-[max(1rem,calc((100vw-1280px)/2+1rem))] scroll-smooth hide-scrollbar snap-x snap-mandatory"
          dir="rtl"
        >
          {products.map((product) => {
            const apiP = apiProducts.find(
              (p) => String(p.id) === String(product.id),
            );
            const views = apiP?.video_views ?? 0;
            const isHovered = hoveredId === Number(product.id);

            return (
              <div
                key={product.id}
                className="flex-shrink-0 snap-start"
                onMouseEnter={() => setHoveredId(Number(product.id))}
                onMouseLeave={() => setHoveredId(null)}
              >
                <button
                  onClick={() =>
                    handlePlayVideo(
                      product,
                      apiProducts.find(
                        (p) => String(p.id) === String(product.id),
                      ),
                    )
                  }
                  className="relative block w-[160px] min-w-[160px] md:w-[300px] aspect-[9/16] rounded-2xl overflow-hidden bg-charcoal-900 group cursor-pointer text-right shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${isHovered ? "scale-110" : "scale-100"}`}
                  />
                  <div className="absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/60 to-transparent">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-[11px] font-semibold bg-[#79A286]/90 backdrop-blur-sm px-2.5 py-0.5 rounded-full">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-80"}`}
                  >
                    <div
                      className={`w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg transition-transform duration-300 ${isHovered ? "scale-110" : "scale-100"}`}
                    >
                      <i className="ri-play-fill text-xl text-charcoal-900 mr-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-20 pb-3 px-3">
                    <p className="text-white text-xs font-medium mb-2.5 line-clamp-2 leading-relaxed">
                      {product.name}
                    </p>
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-2.5 flex items-center gap-2.5 text-right">
                      <div className="w-11 h-11 rounded-lg overflow-hidden bg-cream-100 flex-shrink-0 hidden sm:block">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-charcoal-900 truncate">
                          {product.name}
                        </p>
                        <p className="text-[11px] text-charcoal-500">
                          {product.price} شيكل
                        </p>
                      </div>
                      <div className="flex-shrink-0 bg-charcoal-900 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap">
                        اشتري الآن
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-[72px] left-3 flex items-center gap-1 text-white/80 text-[11px]">
                    <i className="ri-eye-line w-3 h-3 flex items-center justify-center" />
                    {views} مشاهدة
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="container-main mt-4 md:hidden">
        <div className="flex items-center justify-center gap-1.5 text-charcoal-400 text-xs">
          <i className="ri-arrow-right-line" />
          <span>اسحبي للتنقل</span>
          <i className="ri-arrow-left-line" />
        </div>
      </div>

      {/* VideoShoppingModal */}
      <VideoShoppingModal
        video={selectedVideo!}
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />
    </section>
  );
}
